import {
  ChangeDetectionStrategy,
  Component,
  inject,
  type OnDestroy,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { FeatureVisibility } from '@principle-theorem/feature-flags';
import { IIntegration } from '@principle-theorem/integrations';
import { FeatureFlagsService } from '@principle-theorem/ng-feature-flags';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import {
  BasicDialogService,
  DialogPresets,
  isDisabled$,
  TypedFormControl,
  TypedFormGroup,
  type IBreadcrumb,
} from '@principle-theorem/ng-shared';
import {
  Organisation,
  OrganisationCache,
  OrganisationSubscription,
  Practice,
} from '@principle-theorem/principle-core';
import {
  IOrganisationSubscription,
  IPractice,
  IStaffer,
  type IOrganisation,
  type IUser,
} from '@principle-theorem/principle-core/interfaces';
import {
  all$,
  DocumentReference,
  filterUndefined,
  findProp,
  Firestore,
  HISTORY_DATE_FORMAT,
  isSameRef,
  shareReplayCold,
  snapshot,
  type CollectionReference,
  type WithRef,
} from '@principle-theorem/shared';
import { ITemporaryToken } from '@principle-theorem/temporary-tokens';
import { sortBy } from 'lodash';
import { from, Subject, type Observable } from 'rxjs';
import { map, switchMap, take, takeUntil } from 'rxjs/operators';
import {
  IOrganisationSubscriptionDialogRequest,
  IOrganisationSubscriptionDialogResponse,
  IOrganisationSubscriptionFormData,
  OrganisationSubscriptionDialogComponent,
} from './organisation-subscription-dialog/organisation-subscription-dialog.component';

@Component({
  selector: 'pr-organisation-edit',
  templateUrl: './organisation-edit.component.html',
  styleUrls: ['./organisation-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class OrganisationEditComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  private _dialog = inject(BasicDialogService);
  private _snackBar = inject(MatSnackBar);
  private _route = inject(ActivatedRoute);
  private _router = inject(Router);
  private _featureFlags = inject(FeatureFlagsService);
  private _organisationService = inject(OrganisationService);
  breadcrumbs$: Observable<IBreadcrumb[]>;
  organisation$: Observable<WithRef<IOrganisation>>;
  integrationsColRef$: Observable<CollectionReference<IIntegration>>;
  temporaryTokensColRef$: Observable<CollectionReference<ITemporaryToken>>;
  organisations$: Observable<WithRef<IOrganisation>[]>;
  subscriptions$: Observable<WithRef<IOrganisationSubscription>[]>;
  users$: Observable<WithRef<IUser>[]>;
  userCollection$: Observable<CollectionReference<IUser>>;
  managementStaff$: Observable<WithRef<IStaffer>[]>;
  optInFeatures: string[];

  form = new TypedFormGroup<
    Pick<IOrganisation, 'optInFeatures' | 'accountManagers'>
  >({
    optInFeatures: new TypedFormControl<string[]>([]),
    accountManagers: new TypedFormControl<DocumentReference<IStaffer>[]>([]),
  });
  isDisabled$ = isDisabled$(this.form);
  readonly dateFormat = HISTORY_DATE_FORMAT;
  subscriptionColumns = [
    'customerId',
    'subscriptionId',
    'practiceRef',
    'meteredBillingEnabled',
    'actions',
  ];

  constructor() {
    this.organisations$ = from(Firestore.getDocs(Organisation.col()));
    this.organisation$ = this._route.data.pipe(
      findProp<WithRef<IOrganisation>>('organisation'),
      filterUndefined(),
      switchMap((organisation) => Firestore.doc$(organisation.ref))
    );
    this.subscriptions$ = this.organisation$.pipe(
      filterUndefined(),
      switchMap((organisation) => Organisation.subscriptions$(organisation))
    );

    this.breadcrumbs$ = this.organisation$.pipe(
      map((organisation) => [
        { label: 'Organisations', path: '../' },
        { label: organisation.name },
      ])
    );

    this.userCollection$ = this.organisation$.pipe(
      filterUndefined(),
      map((organisation) => Organisation.userCol(organisation))
    );

    this.users$ = this.userCollection$.pipe(
      switchMap((collection) => all$(collection)),
      map((users) => sortBy(users, 'name'))
    );

    this.managementStaff$ = this._organisationService.managementStaff$;

    this.organisation$
      .pipe(filterUndefined(), take(1), takeUntil(this._onDestroy$))
      .subscribe((organisation) =>
        this.form.patchValue(
          {
            optInFeatures: organisation.optInFeatures ?? [],
            accountManagers: organisation.accountManagers ?? [],
          },
          { emitEvent: false }
        )
      );

    this.optInFeatures = this._featureFlags.features
      .byVisibility(FeatureVisibility.OptIn)
      .map((feature) => feature.label)
      .sort();

    this.integrationsColRef$ = this.organisation$.pipe(
      map((organisation) => Organisation.integrationCol(organisation)),
      shareReplayCold()
    );

    this.temporaryTokensColRef$ = this.organisation$.pipe(
      map((organisation) => Organisation.temporaryTokenCol(organisation)),
      shareReplayCold()
    );
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  async getPracticeName(
    practiceRef: DocumentReference<IPractice>
  ): Promise<string> {
    const practice = await OrganisationCache.practices.getDoc(practiceRef);
    const brand = await OrganisationCache.brands.getDoc(
      Practice.brandRef(practice)
    );
    return `${brand.name} - ${practice.name}`;
  }

  async save(): Promise<void> {
    if (!this.form.valid) {
      return;
    }
    const organisation = await snapshot(this.organisation$);
    const data = this.form.getRawValue();

    await Firestore.patchDoc(organisation.ref, {
      optInFeatures: data.optInFeatures || [],
      accountManagers: data.accountManagers || [],
    });
    this._snackBar.open('Settings Updated');
  }

  async addSubscription(): Promise<void> {
    const organisation = await snapshot(this.organisation$);
    const subscription = await this._openSubscriptionDialog(organisation);
    if (!subscription) {
      return;
    }
    await Firestore.addDoc(
      Organisation.subscriptionCol(organisation),
      OrganisationSubscription.init(subscription)
    );
    this._snackBar.open('Settings Updated');
  }

  async editSubscription(
    subscription: WithRef<IOrganisationSubscription>
  ): Promise<void> {
    const organisation = await snapshot(this.organisation$);

    const updatedSubscription = await this._openSubscriptionDialog(
      organisation,
      subscription
    );
    if (!updatedSubscription) {
      return;
    }
    await Firestore.patchDoc(subscription.ref, updatedSubscription);
    this._snackBar.open('Settings Updated');
  }

  async deleteSubscription(
    subscription: WithRef<IOrganisationSubscription>
  ): Promise<void> {
    await Firestore.deleteDoc(subscription.ref);
    this._snackBar.open('Subscription Deleted');
  }

  async setOrganisationSelected(
    organisation?: WithRef<IOrganisation>
  ): Promise<void> {
    if (!organisation) {
      return;
    }

    await this._router.navigate(['../', organisation.ref.id], {
      relativeTo: this._route,
    });

    this._snackBar.open(`Switched to organisation: ${organisation.name}`);
  }

  compareStafferRefs(
    stafferA?: DocumentReference<IStaffer>,
    stafferB?: DocumentReference<IStaffer>
  ): boolean {
    return isSameRef(stafferA, stafferB);
  }

  private async _openSubscriptionDialog(
    organisation: WithRef<IOrganisation>,
    subscription?: WithRef<IOrganisationSubscription>
  ): Promise<IOrganisationSubscriptionFormData | undefined> {
    const response = await this._dialog
      .open<
        OrganisationSubscriptionDialogComponent,
        IOrganisationSubscriptionDialogRequest,
        IOrganisationSubscriptionDialogResponse
      >(
        OrganisationSubscriptionDialogComponent,
        DialogPresets.medium({
          data: { organisation, subscription },
        })
      )
      .afterClosed()
      .toPromise();
    return response?.subscription;
  }
}
